import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { Button } from "@primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/primitives/dialog";
import CurrentEmailStep from "./steps/CurrentEmailStep";
import NewEmailStep from "./steps/NewEmailStep";
import EmailChangeCodeStep from "./steps/EmailChangeCodeStep";
import { queryClient } from "@/queryClient";
import { QUERY_KEYS } from "@/config/queryKeys";

type Step = "current" | "new-email" | "code" | "unavailable";

interface StepState {
  step: Step;
  newEmail: string;
  initialCooldown: number;
}

export default function EmailChangeWizard() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<StepState>({
    step: "current",
    newEmail: "",
    initialCooldown: 60,
  });

  function handleClose() {
    setOpen(false);
    resetState();
  }

  function resetState() {
    setState({ step: "current", newEmail: "", initialCooldown: 60 });
  }

  function handleOpenChange(value: boolean) {
    setOpen(value);
    if (!value) {
      resetState();
    }
  }

  function handleCodeSuccess(maskedEmail: string) {
    handleClose();
    // Drop every cached auth-email entry (prefix match covers the
    // username-scoped keys) so the Profile view refetches the new address
    // instead of serving the infinitely-fresh old one.
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.authEmail });
    toast({
      description: t("changeEmail.success", { email: maskedEmail }),
    });
  }

  function handleBackToNewEmail() {
    setState((prev) => ({ ...prev, step: "new-email" }));
  }

  // BE kill-switch (EMAIL_CHANGE_ENABLED=false) answered EmailChange.NotEnabled
  // mid-flow — replace the form with a "temporarily unavailable" state.
  function handleUnavailable() {
    setState((prev) => ({ ...prev, step: "unavailable" }));
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-normal">
          {t("changeEmail.title")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="h-full min-w-full p-6 md:h-auto md:min-w-[43rem]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{t("changeEmail.title")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("changeEmail.description")}
        </DialogDescription>

        {state.step === "current" && (
          <CurrentEmailStep
            onContinue={() =>
              setState((prev) => ({ ...prev, step: "new-email" }))
            }
          />
        )}

        {state.step === "new-email" && (
          <NewEmailStep
            onSuccess={(newEmail, initialCooldown) =>
              setState({ step: "code", newEmail, initialCooldown })
            }
            onUnavailable={handleUnavailable}
          />
        )}

        {state.step === "code" && (
          <EmailChangeCodeStep
            newEmail={state.newEmail}
            initialCooldown={state.initialCooldown}
            onSuccess={handleCodeSuccess}
            onExpired={handleBackToNewEmail}
            onEmailTaken={handleBackToNewEmail}
            onUnavailable={handleUnavailable}
          />
        )}

        {state.step === "unavailable" && (
          <div className="my-7 space-y-4">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold">
                {t("changeEmail.title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t("errors.EmailChange.NotEnabled")}
              </p>
            </div>
            <Button onClick={handleClose} className="h-12 w-full rounded-lg">
              {t("close")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
